import { Fragment, type ReactNode, useState } from 'react';
import type { Key, SortDescriptor } from 'react-aria-components';
import { msg } from '@gonfalon/messages/msg';
import { IconButton, Menu, MenuItem, MenuTrigger, Popover, Separator } from '@launchpad-ui/components';

import { DataTableActionContent } from './DataTableActionContent';
import type { ResolvedColumn } from './useDataTableColumns';

import styles from './DataTable.module.css';

type Props<T> = {
  column: ResolvedColumn<T>;
  defaultColumnActionsEnabled: boolean;
  sortDescriptor?: SortDescriptor;
  canHide: boolean;
  isHideDisabled: boolean;
  canRemoveSort: boolean;
  canResize: boolean;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onSort: (direction?: 'ascending' | 'descending') => void;
  onHide: () => void;
  onResetSize: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
};

export function DataTableColumnHeaderActions<T>({
  column,
  defaultColumnActionsEnabled,
  sortDescriptor,
  canHide,
  isHideDisabled,
  canRemoveSort,
  canResize,
  canMoveLeft,
  canMoveRight,
  onSort,
  onHide,
  onResetSize,
  onMoveLeft,
  onMoveRight,
}: Props<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const callbacks = typeof column.headerActions === 'object' ? column.headerActions : undefined;
  const isSorted = sortDescriptor?.column === column.id;
  const customActions = callbacks?.actions ?? [];

  const handleAction = (key: Key) => {
    switch (key) {
      case 'sort-descending':
        onSort('descending');
        break;
      case 'sort-ascending':
        onSort('ascending');
        break;
      case 'remove-sort':
        onSort();
        break;
      case 'update-label':
        callbacks?.onUpdateLabel?.(column.ref);
        break;
      case 'copy-search-key':
        callbacks?.onCopySearchKey?.(column.ref);
        break;
      case 'reset-size':
        if (defaultColumnActionsEnabled) {
          onResetSize();
        } else {
          callbacks?.onResetSize?.(column.ref);
        }
        break;
      case 'move-left':
        onMoveLeft();
        break;
      case 'move-right':
        onMoveRight();
        break;
      case 'hide-column':
        onHide();
        break;
      default:
        customActions.find((action) => action.id === key)?.onAction(column.ref);
        break;
    }
  };

  const sortItems: ReactNode[] = column.allowsSorting
    ? [
        <MenuItem
          key="sort-descending"
          id="sort-descending"
          isDisabled={isSorted && sortDescriptor?.direction === 'descending'}
        >
          <DataTableActionContent icon="sort-desc">
            {msg('ui.dataTable.columnMenu.sortDescending')}
          </DataTableActionContent>
        </MenuItem>,
        <MenuItem
          key="sort-ascending"
          id="sort-ascending"
          isDisabled={isSorted && sortDescriptor?.direction === 'ascending'}
        >
          <DataTableActionContent icon="sort-asc">
            {msg('ui.dataTable.columnMenu.sortAscending')}
          </DataTableActionContent>
        </MenuItem>,
        ...(isSorted && canRemoveSort
          ? [
              <MenuItem key="remove-sort" id="remove-sort">
                <DataTableActionContent icon="cancel-circle-outline">
                  {msg('ui.dataTable.columnMenu.removeSort')}
                </DataTableActionContent>
              </MenuItem>,
            ]
          : []),
      ]
    : [];
  const moveItems: ReactNode[] = [
    ...(canMoveLeft
      ? [
          <MenuItem key="move-left" id="move-left">
            <DataTableActionContent icon="chevron-left">
              {msg('ui.dataTable.columnMenu.moveLeft')}
            </DataTableActionContent>
          </MenuItem>,
        ]
      : []),
    ...(canMoveRight
      ? [
          <MenuItem key="move-right" id="move-right">
            <DataTableActionContent icon="chevron-right">
              {msg('ui.dataTable.columnMenu.moveRight')}
            </DataTableActionContent>
          </MenuItem>,
        ]
      : []),
  ];
  const columnItems: ReactNode[] = [
    ...(callbacks?.onUpdateLabel
      ? [
          <MenuItem key="update-label" id="update-label">
            <DataTableActionContent icon="edit">{msg('ui.dataTable.columnMenu.updateLabel')}</DataTableActionContent>
          </MenuItem>,
        ]
      : []),
    ...(callbacks?.onCopySearchKey
      ? [
          <MenuItem key="copy-search-key" id="copy-search-key">
            <DataTableActionContent icon="copy-content">
              {msg('ui.dataTable.columnMenu.copySearchKey')}
            </DataTableActionContent>
          </MenuItem>,
        ]
      : []),
    ...(!defaultColumnActionsEnabled && callbacks?.onResetSize
      ? [
          <MenuItem key="reset-size" id="reset-size">
            <DataTableActionContent icon="sync">{msg('ui.dataTable.columnMenu.resetSize')}</DataTableActionContent>
          </MenuItem>,
        ]
      : []),
  ];
  const sizeItems: ReactNode[] =
    defaultColumnActionsEnabled && canResize
      ? [
          <MenuItem key="reset-size" id="reset-size">
            <DataTableActionContent icon="sync">{msg('ui.dataTable.columnMenu.resetSize')}</DataTableActionContent>
          </MenuItem>,
        ]
      : [];
  const customItems: ReactNode[] = customActions.map((action) => (
    <MenuItem key={String(action.id)} id={action.id} isDisabled={action.isDisabled}>
      <DataTableActionContent icon={action.icon}>{action.label}</DataTableActionContent>
    </MenuItem>
  ));
  const hideItems: ReactNode[] =
    !defaultColumnActionsEnabled || canHide
      ? [
          <MenuItem
            key="hide-column"
            id="hide-column"
            isDisabled={defaultColumnActionsEnabled ? isHideDisabled : !canHide}
          >
            <DataTableActionContent icon="cancel-circle-outline">
              {msg('ui.dataTable.columnMenu.hideColumn', { columnName: column.name })}
            </DataTableActionContent>
          </MenuItem>,
        ]
      : [];
  const groups = [[...sortItems, ...columnItems], sizeItems, moveItems, customItems, hideItems].filter(
    (group) => group.length > 0,
  );

  if (defaultColumnActionsEnabled && groups.length === 0) {
    return null;
  }

  return (
    <div
      className={styles.headerActions}
      data-default-column-actions={defaultColumnActionsEnabled || undefined}
      data-open={isOpen || undefined}
      draggable={false}
      onDragStart={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <MenuTrigger onOpenChange={setIsOpen}>
        <IconButton
          icon="chevron-down"
          aria-label={msg('ui.dataTable.columnMenu.triggerLabel', { columnName: column.name })}
          variant="minimal"
          size="small"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        />
        <Popover placement="bottom end">
          <Menu onAction={handleAction}>
            {groups.map((group, index) => (
              <Fragment key={index}>
                {index > 0 && <Separator />}
                {group}
              </Fragment>
            ))}
          </Menu>
        </Popover>
      </MenuTrigger>
    </div>
  );
}
